import { AISHA_API_BASE } from '@/constants';

export interface ArtisanOrder {
  id: number;
  user_id: string;
  product_name: string;
  variation_name: string;
  with_installation: boolean;
  product_price: number;
  installation_price: number;
  tax: number;
  total_amount: number;
  image_url: string;
  shipping_name: string;
  shipping_email: string;
  shipping_phone: string;
  shipping_zip: string;
  shipping_state: string;
  shipping_city: string;
  shipping_address1: string;
  shipping_address2: string;
  stripe_payment_intent_id: string;
  estimated_delivery_date: string | null;
  shipped_date: string | null;
  shipping_carrier: string;
  tracking_number: string;
  review_rating: number | null;
  review_comment: string;
  review_image_url: string;
  reviewed_at: string | null;
  status: 'new' | 'confirming' | 'manufacturing' | 'shipped' | 'reviewed' | 'cancelled';
  admin_notes: string;
  memo: string;
  created_at: string;
  updated_at: string;
}

export const STATUS_LABELS: Record<string, string> = {
  new: '新規注文',
  confirming: 'お客様確認',
  manufacturing: '製造中',
  shipped: '発送済',
  reviewed: '到着・評価',
  cancelled: 'キャンセル',
};

// ステータス進行順序（キャンセル除く）
export const STATUS_STEPS = [
  { key: 'new', label: '新規注文' },
  { key: 'confirming', label: 'お客様確認' },
  { key: 'manufacturing', label: '製造中' },
  { key: 'shipped', label: '発送済' },
  { key: 'reviewed', label: '到着・評価' },
] as const;

export const getStatusStepIndex = (status: string): number => {
  const idx = STATUS_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : -1;
};

export const STATUS_COLORS: Record<string, string> = {
  new: 'bg-red-900/50 text-red-400 border-red-700',
  confirming: 'bg-yellow-900/50 text-yellow-400 border-yellow-700',
  manufacturing: 'bg-blue-900/50 text-blue-400 border-blue-700',
  shipped: 'bg-green-900/50 text-green-400 border-green-700',
  reviewed: 'bg-purple-900/50 text-purple-400 border-purple-700',
  cancelled: 'bg-gray-700 text-gray-400 border-gray-600',
};

// Artisan注文一覧を取得
export const getArtisanOrders = async (status?: string): Promise<ArtisanOrder[]> => {
  try {
    const params = new URLSearchParams();
    if (status) {
      params.append('status', status);
    }
    const queryString = params.toString();
    const url = `${AISHA_API_BASE}/admin/artisan-orders/${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisan注文一覧の取得に失敗しました');
    }
  } catch (error) {
    console.error('Artisan注文一覧取得エラー:', error);
    throw error;
  }
};

// Artisan注文詳細を取得
export const getArtisanOrder = async (id: number): Promise<ArtisanOrder> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/admin/artisan-orders/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisan注文詳細の取得に失敗しました');
    }
  } catch (error) {
    console.error('Artisan注文詳細取得エラー:', error);
    throw error;
  }
};

// Artisan注文を更新（ステータス・メモ・納期・配送情報）
export const updateArtisanOrderStatus = async (
  id: number,
  status: string,
  admin_notes?: string,
  extra?: { estimated_delivery_date?: string; shipped_date?: string; shipping_carrier?: string; tracking_number?: string }
): Promise<ArtisanOrder> => {
  try {
    const body: Record<string, string> = { status };
    if (admin_notes !== undefined) {
      body.admin_notes = admin_notes;
    }
    if (extra?.estimated_delivery_date !== undefined) {
      body.estimated_delivery_date = extra.estimated_delivery_date;
    }
    if (extra?.shipped_date !== undefined) {
      body.shipped_date = extra.shipped_date;
    }
    if (extra?.shipping_carrier !== undefined) {
      body.shipping_carrier = extra.shipping_carrier;
    }
    if (extra?.tracking_number !== undefined) {
      body.tracking_number = extra.tracking_number;
    }

    const response = await fetch(`${AISHA_API_BASE}/admin/artisan-orders/${id}/status/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisan注文ステータスの更新に失敗しました');
    }
  } catch (error) {
    console.error('Artisan注文ステータス更新エラー:', error);
    throw error;
  }
};
